import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, CheckCircle2, ExternalLink, TrendingUp, Target, Brain, Search, Sparkles, Info, AlertTriangle } from "lucide-react";
import ProjectDisclaimer from "@/components/ProjectDisclaimer";
import ResearchFoundation from "@/components/ResearchFoundation";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { trackPageView } from "@/lib/analytics";
import { jsonLdCitations } from "@/data/research-sources";

export default function Methodology() {
  
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash === '#whats-new') {
      setTimeout(() => {
        document.getElementById('whats-new')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
    
    trackPageView('/methodology', 'Methodology - AILens How We Calculate');
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <title>AILens Methodology | How We Score AI Visibility</title>
        <meta name="description" content="Transparent, research-based scoring methodology. AI Visibility (40%) + Google Search (30%) + Additional Platforms (30%). Based on NeurIPS and KDD research." />
        <link rel="canonical" href="https://ai-lens.app/methodology" />
        
        {/* Open Graph */}
        <meta property="og:title" content="AILens Methodology - Research-Based AI Visibility Scoring" />
        <meta property="og:description" content="Built on academic research from Princeton and University of Toronto. See how we calculate AI visibility." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://ai-lens.app/methodology" />
        <meta property="og:image" content="https://ai-lens.app/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AILens Methodology - Research-Based Scoring" />
        <meta name="twitter:description" content="Built on academic research from Princeton and University of Toronto." />
        <meta name="twitter:image" content="https://ai-lens.app/og-image.png" />
        
        {/* JSON-LD Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How does AILens calculate the AI Visibility Score?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "AILens combines three weighted factors: ChatGPT Visibility (40%), Google Search Results (30%), and Additional Platforms (30%). The automated scan measures 70 of 100 possible points."
                }
              },
              {
                "@type": "Question",
                "name": "What AI platforms does AILens scan?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "AILens scans ChatGPT and Google in the automated scan. Additional AI platforms require extended analysis."
                }
              },
              {
                "@type": "Question",
                "name": "Is AILens free to use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, AILens offers an automated scan with no signup required."
                }
              }
            ]
          })}
        </script>
        
        {/* JSON-LD Structured Data - TechArticle */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "AILens Scoring Methodology",
            "description": "Research-based methodology for measuring AI visibility, built on academic studies from KDD and NeurIPS conferences, plus supporting preprints.",
            "author": {
              "@type": "Organization",
              "name": "AILens",
              "url": "https://ai-lens.app"
            },
            "datePublished": "2026-01-01",
            "dateModified": "2026-02-16",
            "publisher": {
              "@type": "Organization",
              "name": "AILens",
              "url": "https://ai-lens.app"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://ai-lens.app/methodology"
            },
            "about": [
              {"@type": "Thing", "name": "AI Visibility"},
              {"@type": "Thing", "name": "Generative Engine Optimization"},
              {"@type": "Thing", "name": "Search Engine Optimization"}
            ],
            "citation": jsonLdCitations()
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" size="sm" className="gap-2" onClick={() => {
            if (window.history.length > 1) {
              window.history.back();
            } else {
              window.location.href = '/';
            }
          }}>
            <ArrowLeft className="h-4 w-4" />
            Back to Scanner
          </Button>
        </div>
      </header>

      {/* Hero - No gradient, clean border bottom */}
      <section className="bg-background border-b border-border py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            AILens Methodology
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            How we calculate your AI Visibility Score with transparent, 
            research-backed analysis
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12 space-y-8">
        
        {/* Disclaimer */}
        <section>
          <p className="text-sm italic text-muted-foreground mb-3">
            Last updated: February 2026
          </p>
          <div className="bg-muted/50 border border-border rounded-lg p-4 flex gap-3">
            <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Note:</strong> This represents our current scoring framework featuring Discovery Mode queries and reward-based scoring. We've aimed for full transparency in our scoring approach.
            </p>
          </div>
        </section>

        <Separator />

        {/* Overview */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">Overview</h2>
          <p className="text-muted-foreground leading-relaxed">
            Your AI Visibility Score combines 2 real-time analyses (ChatGPT + Google Search) to show how your brand appears across the AI landscape. An additional 30 points from other AI platforms are part of the extended scoring framework but are not included in the automated scan.
          </p>
        </section>

        <Separator />

        {/* SEO & GEO: Understanding the Connection */}
        <section>
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                SEO & GEO: How They Work Together
              </h2>
              
              <div className="space-y-4 text-muted-foreground">
                <p>
                  You might have heard the term <strong className="text-foreground">GEO (Generative Engine Optimization)</strong> alongside 
                  traditional SEO. Here's what you need to know:
                </p>
                
                <div className="bg-muted/50 p-4 border-l-4 border-primary">
                  <p className="font-semibold text-foreground mb-2">
                    The Bottom Line:
                  </p>
                  <p>
                    Strong SEO is the foundation for AI visibility. Peer-reviewed research confirms 
                    traditional SEO yields 7× more visibility improvement than GEO-only tactics 
                    (+2.77 vs +0.36 rank gain). You can't skip SEO and jump to GEO.
                  </p>
                  <p className="text-xs mt-2">
                    <a href="https://arxiv.org/abs/2506.11097" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Source: Puerto et al., NeurIPS 2025
                    </a>
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="bg-background p-4 border">
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Search className="h-5 w-5 text-primary" />
                      Traditional SEO
                    </h3>
                    <ul className="space-y-2 text-sm">
                      <li>• Ranking in Google Search results</li>
                      <li>• Technical optimization</li>
                      <li>• Backlinks and authority</li>
                      <li>• Keyword targeting</li>
                    </ul>
                  </div>
                  
                  <div className="bg-background p-4 border">
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      GEO (Generative Engine Optimization)
                    </h3>
                    <ul className="space-y-2 text-sm">
                      <li>• Getting cited in AI responses</li>
                      <li>• Structured, citation-friendly content</li>
                      <li>• Statistics and expert quotes</li>
                      <li>• Fresh, updated content</li>
                    </ul>
                  </div>
                </div>
                
                <p className="mt-4">
                  <strong className="text-foreground">What AILens does:</strong> We measure your performance in both worlds. 
                  Your Google ranking (SEO) shows your search visibility, and your ChatGPT 
                  presence shows your AI visibility (GEO). Both matter.
                </p>
                
                <div className="text-xs mt-4 p-3 bg-muted/30 border">
                  <p className="font-medium text-foreground mb-1">Note on terminology:</p>
                  <p>
                    GEO is also sometimes called AEO (Answer Engine Optimization). 
                    They mean the same thing: optimizing for AI-powered search platforms.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator />

        {/* AI Visibility Analysis */}
        <section>
          <Card>
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-[hsl(var(--brand-purple))]">
                  <Brain className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">AI Visibility Analysis</CardTitle>
                  <CardDescription className="text-base mt-1">Weight: 40% of total score (automated scan)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-foreground mb-3">What We Analyze</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    Does ChatGPT mention your brand when searching?
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    How prominently are you featured in responses?
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    Are competitors mentioned instead of you?
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">How It Works</h4>
                <p className="text-muted-foreground">
                  We use GPT-4 with real-time web search capability to simulate 
                  how ChatGPT discovers and presents brand information. The 
                  analysis evaluates presence, context, and prominence.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-3">AI Discovery Levels</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-4 bg-success/10 p-3 border border-success/30">
                    <span className="text-xl font-bold text-success w-16">+40</span>
                    <span className="text-muted-foreground">Recommended in discovery queries</span>
                  </div>
                  <div className="flex items-center gap-4 bg-warning/10 p-3 border border-warning/30">
                    <span className="text-xl font-bold text-warning w-16">+20</span>
                    <span className="text-muted-foreground">Recognized, not yet recommended</span>
                  </div>
                  <div className="flex items-center gap-4 bg-muted/30 p-3 border">
                    <span className="text-xl font-bold text-muted-foreground w-16">0</span>
                    <span className="text-muted-foreground">Not found by AI</span>
                  </div>
                </div>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                  Our analysis runs two distinct checks. First, a <strong className="text-foreground">Discovery query</strong>: we ask ChatGPT to recommend the best solutions in your category (e.g., "What are the best cleaning service software options?"). If your brand appears, you earn the full 40 points. If not, we run a <strong className="text-foreground">Recognition query</strong>: we ask ChatGPT directly about your brand. If AI knows who you are but didn't recommend you in discovery, you earn 20 points. This "recognized but not recommended" status reveals the exact gap where GEO optimization has the most impact.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">Why 40% Weight?</h4>
                <p className="text-muted-foreground">
                  At the time of this research (early 2026), ChatGPT was the dominant platform for AI-powered brand discovery. Combined with additional AI platforms (30% of total score, not measured in the automated scan), the theoretical AI component represents 70% of the total visibility framework.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Google Search Rankings */}
        <section>
          <Card>
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-[hsl(var(--brand-purple))]">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">Google Search Rankings</CardTitle>
                  <CardDescription className="text-base mt-1">Weight: 30% of total score</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-foreground mb-3">What We Check</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    Does YOUR domain appear in Google's top 10?
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    For your target keyword (generic search)?
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    What's your precise ranking position?
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">How We Search</h4>
                <p className="text-muted-foreground">
                  AILens performs a generic keyword search (e.g., "email marketing software") without 
                  including the brand name. This measures true competitive visibility: whether your site 
                  ranks for the category, not just for your own brand name. Results may vary based on 
                  keyword phrasing. For benchmark consistency, all searches are performed via US-based servers (google.com), which may return different results than local Google domains.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">Domain Verification</h4>
                <p className="text-muted-foreground">
                  We verify that the ranking result matches YOUR domain, not a 
                  competitor or review site. Without domain verification, results 
                  would be misleading.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">Why 30% Weight?</h4>
                <p className="text-muted-foreground">
                  Research shows ~40% of AI-cited sources don't appear in Google's top organic 
                  results. AI engines use different selection criteria. Your Google ranking 
                  remains critical as the foundation for being cited by AI platforms.
                </p>
                <p className="text-xs mt-2">
                  <a href="https://arxiv.org/abs/2510.11560" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Source: Kirsten et al., arXiv 2025
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator />

        {/* Scoring Formula */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">Our Scoring Approach</h2>
          
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="bg-muted/50 p-4 md:p-6 text-center mb-4 border">
                <code className="text-base md:text-lg font-mono text-foreground">
                  Score = (ChatGPT × 0.40) + (Google × 0.30) + (Additional Platforms × 0.30)
                </code>
              </div>
              
              <p className="text-sm text-muted-foreground mb-6 text-center italic">
                This is our core formula focused on ChatGPT's role (as of early 2026) as the primary AI discovery channel. 
                This methodology was developed based on real-world data and academic research.
              </p>

              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-3">Why This Weighting?</h4>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-3 bg-muted/30 p-3 border">
                    <span className="font-bold text-primary min-w-[60px]">40%</span>
                    <div>
                      <span className="font-medium text-foreground">ChatGPT</span>: Direct AI visibility, where users ask questions
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-muted/30 p-3 border">
                    <span className="font-bold text-primary min-w-[60px]">30%</span>
                    <div>
                      <span className="font-medium text-foreground">Google</span>: SEO foundation, where AI platforms source content
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-muted/30 p-3 border">
                    <span className="font-bold text-muted-foreground min-w-[60px]">30%</span>
                    <div>
                      <span className="font-medium text-foreground">Additional AI Platforms</span>: Measured via extended analysis
                    </div>
                  </div>
                </div>
              </div>
              
                <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-warning/10 border-2 border-warning p-4">
                  <h5 className="font-semibold text-foreground mb-1">Automated Scan</h5>
                  <p className="text-2xl font-bold text-warning mb-2">Max 70 pts</p>
                  <p className="text-sm text-muted-foreground">
                    ChatGPT (40) + Google (30). Maximum automated scan score: 70.
                  </p>
                </div>
                <div className="bg-success/10 border-2 border-success p-4">
                  <h5 className="font-semibold text-foreground mb-1">Extended Analysis</h5>
                  <p className="text-2xl font-bold text-success mb-2">100 pts (theoretical)</p>
                  <p className="text-sm text-muted-foreground">
                    +30 from additional AI platforms.
                  </p>
                </div>
              </div>

            </CardContent>
          </Card>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Google Position Rewards</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Our scoring uses a <strong className="text-foreground">reward model</strong>: no penalties, only points for strong positions:
            </p>
            <div className="grid gap-3">
              <div className="flex items-center gap-4 bg-success/10 p-3 border border-success/30">
                <span className="text-xl font-bold text-success w-16">+30</span>
                <span className="text-muted-foreground">Position 1-3: AI citation zone, highest visibility</span>
              </div>
              <div className="flex items-center gap-4 bg-warning/10 p-3 border border-warning/30">
                <span className="text-xl font-bold text-warning w-16">+20</span>
                <span className="text-muted-foreground">Position 4-10: Page 1, solid SEO foundation</span>
              </div>
              <div className="flex items-center gap-4 bg-muted/30 p-3 border">
                <span className="text-xl font-bold text-muted-foreground w-16">+10</span>
                <span className="text-muted-foreground">Position 11-20: On the radar, room to improve</span>
              </div>
              <div className="flex items-center gap-4 bg-muted/30 p-3 border">
                <span className="text-xl font-bold text-muted-foreground w-16">0</span>
                <span className="text-muted-foreground">Position 21+ or not found: Not effectively visible</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground italic">
              Research shows top 20 domains capture 67% of all AI citations. Higher positions yield exponentially more visibility.
              <a href="https://arxiv.org/abs/2507.05301" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                (Yang, arXiv 2025)
              </a>
            </p>
          </div>
        </section>

        <Separator />

        {/* Automated Scan vs. Extended Analysis */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">Automated Scan vs. Extended Analysis</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Automated Scan Card */}
            <Card className="border-2 border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="text-amber-500">Automated Scan</span>
                  <span className="text-sm font-normal text-muted-foreground">(Max 70 points)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Evaluates your visibility across ChatGPT recommendations and traditional search:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm">
                    <span className="font-bold text-primary min-w-[60px]">40 pts</span>
                    <div>
                      <span className="font-medium text-foreground">ChatGPT</span>
                      <p className="text-muted-foreground">AI-powered brand discovery and recommendations.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <span className="font-bold text-primary min-w-[60px]">30 pts</span>
                    <div>
                      <span className="font-medium text-foreground">Google Search</span>
                      <p className="text-muted-foreground">Your ranking position on Google Search.</p>
                    </div>
                  </li>
                </ul>
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300 italic">
                    The automated scan covers 70 of 100 possible points. The remaining 30 points require extended manual analysis of additional AI platforms.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Extended Analysis Card */}
            <Card className="border-2 border-emerald-200 dark:border-emerald-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="text-emerald-500">Extended Analysis</span>
                  <span className="text-sm font-normal text-muted-foreground">(Max 100 points)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Adds additional AI platforms to complete your visibility picture.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm">
                    <span className="font-bold text-muted-foreground min-w-[60px]">30 pts</span>
                    <div>
                      <span className="font-medium text-foreground">Additional AI platforms</span>
                      <p className="text-muted-foreground">Included in extended analysis</p>
                    </div>
                  </li>
                </ul>
                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 rounded-lg">
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 italic">
                    Additional AI platforms provide a more complete picture of AI visibility.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator />

        {/* Score Sensitivity Section */}
        <section>
          <h2 className="text-xl font-semibold text-foreground mb-4">Why Scores Vary (Not a Vanity Metric)</h2>
          
          <p className="text-muted-foreground mb-6 leading-relaxed">
            Your AILens score is designed to be sensitive to actual performance. Unlike vanity metrics 
            that flatter every user with high numbers, this score will drop if your SEO foundation is 
            weak or if AI models don't recognize your brand. It's calculated from live API queries, 
            not cached estimates.
          </p>

          {/* Score Range Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse border border-border text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="border border-border px-3 py-2 text-left font-medium">Score Range</th>
                  <th className="border border-border px-3 py-2 text-left font-medium">Status</th>
                  <th className="border border-border px-3 py-2 text-left font-medium">What It Indicates</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border px-3 py-2 font-semibold">50-70</td>
                  <td className="border border-border px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-success" />
                      Strong Foundation
                    </span>
                  </td>
                  <td className="border border-border px-3 py-2 text-muted-foreground">
                    Visible on scanned platforms.
                  </td>
                </tr>
                <tr className="bg-muted/30">
                  <td className="border border-border px-3 py-2 font-semibold">30-49</td>
                  <td className="border border-border px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-warning" />
                      Partial Visibility
                    </span>
                  </td>
                  <td className="border border-border px-3 py-2 text-muted-foreground">
                    Room for improvement. Consider SEO and GEO optimization.
                  </td>
                </tr>
                <tr>
                  <td className="border border-border px-3 py-2 font-semibold">1-29</td>
                  <td className="border border-border px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-destructive" />
                      Low Visibility
                    </span>
                  </td>
                  <td className="border border-border px-3 py-2 text-muted-foreground">
                    Significant optimization needed on both SEO and AI platforms.
                  </td>
                </tr>
                <tr className="bg-muted/30">
                  <td className="border border-border px-3 py-2 font-semibold">0</td>
                  <td className="border border-border px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground" />
                      Not Detected
                    </span>
                  </td>
                  <td className="border border-border px-3 py-2 text-muted-foreground">
                    Starting point. Build your SEO foundation first.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sensitivity Test Callout */}
          <div className="bg-muted/50 border-l-4 border-primary p-4 mb-6">
            <h4 className="font-semibold text-foreground mb-2">The Sensitivity Test</h4>
            <p className="text-muted-foreground text-sm mb-3">
              Two brands in the exact same industry will get completely different scores:
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <strong className="text-foreground">Brand A (Score 68):</strong> Invested in Entity SEO, 
                Schema markup, and citation-friendly content.
              </li>
              <li>
                <strong className="text-foreground">Brand B (Score 24):</strong> Relies on outdated keyword 
                strategies with no structured data.
              </li>
            </ul>
            <p className="text-sm text-muted-foreground mt-3">
              The score exists to highlight this gap, so you can fix it.
            </p>
          </div>
        </section>

        <Separator />

        {/* Research Foundation - Unified Component */}
        <ResearchFoundation />

        <Separator />

        {/* Important Limitations */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-warning" />
            Important Limitations
          </h2>
          
          <div className="space-y-4 text-muted-foreground">
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">AI responses are non-deterministic</h4>
              <p>
                The same brand may score differently on consecutive scans due to the probabilistic nature 
                of large language models. Scores represent a single point-in-time snapshot, not a stable metric.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Google position depends on keyword phrasing</h4>
              <p>
                A brand may rank highly for "email marketing" but not appear in the top 10 for 
                "email marketing software." Position data reflects the specific keyword entered during the scan.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Geographic variation affects results</h4>
              <p>
                Search results from Google and responses from AI platforms may differ based on the 
                user's location and language settings.
              </p>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <p className="text-foreground font-medium">
                This tool is best used as a directional indicator of AI visibility trends, not as a 
                definitive ranking system.
              </p>
            </div>
          </div>
        </section>

        <Separator />

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                How does the Google search work?
              </h4>
              <p className="text-muted-foreground">
                AILens searches for your keyword generically (e.g., "shoes") without 
                including your brand name. This tests competitive ranking in your 
                market. Searching "Nike shoes" would always find Nike.com, 
                but doesn't show if you rank for actual purchase-intent queries 
                against competitors.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                Why is my score 0 if I'm ranked #1 in Google?
              </h4>
              <p className="text-muted-foreground">
                This usually means domain mismatch or Google spelling correction. 
                Check if: (1) The result is your actual domain, not a competitor, 
                or (2) Google autocorrected your brand name, meaning it doesn't 
                recognize it yet.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                What's the maximum automated scan score?
              </h4>
              <p className="text-muted-foreground">
                The theoretical maximum is 70/100 (ChatGPT 40 + Google 30). The ChatGPT component uses discovery-based scoring: 40 points if AI recommends your brand, 20 points if AI recognizes but doesn't recommend you, and 0 if AI doesn't know your brand. Google scoring 
                uses a reward model: Position 1-3 earns 30 points, Position 4-10 earns 20 points, 
                Position 11-20 earns 10 points, and Position 21+ earns 0 points. No penalties: 
                only rewards for strong positions. A score of 55-70 demonstrates strong visibility 
                on the platforms we scan. Extended analysis of additional AI platforms would be needed for the full 100-point score.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                Can I improve my score?
              </h4>
              <p className="text-muted-foreground">
                Yes! Focus on: (1) Ranking in Google's top 3 for your keywords (essential 
                for both SEO and AI citations), (2) Creating more content with your brand 
                name, (3) Building quality backlinks to boost authority (helps both search 
                rankings and AI trust), (4) Keeping content fresh and updated. AI platforms 
                favor recently updated content, and (5) Getting mentioned on authoritative 
                sites that AI platforms trust.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                Is this an SEO tool or a GEO tool?
              </h4>
              <p className="text-muted-foreground">
                Both! AILens helps you understand your visibility in traditional search 
                (SEO) and AI platforms (GEO). Here's the key insight: <strong className="text-foreground">You can't 
                succeed at GEO without good SEO.</strong> Academic research from NeurIPS 2025 proves 
                traditional SEO yields 7× more visibility improvement than GEO-only tactics 
                (+2.77 vs +0.36 rank gain). Strong SEO is the foundation for AI visibility. 
                Think of it this way: SEO gets you into the top results, then GEO techniques 
                help AI platforms choose to cite you.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                What don't you disclose?
              </h4>
              <p className="text-muted-foreground">
                As a completed research project, we don't expose exact API implementations, 
                internal caching strategies, or detailed prompt engineering. However, 
                we're 100% transparent about what we measure, why it matters, how 
                scores are calculated, and the research backing our approach.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">
                How accurate are the results?
              </h4>
              <p className="text-muted-foreground">
                Results reflect real-time data at scan time. AI platforms and search rankings 
                change frequently, so your score is a snapshot, not a guarantee. We use live 
                APIs for maximum accuracy, but AI visibility is inherently dynamic.
              </p>
            </div>
          </div>
        </section>

        {/* What's New */}
        <section id="whats-new" className="space-y-6 scroll-mt-24">
          <h3 className="text-lg font-semibold text-foreground">What's New</h3>

          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">February 2026</p>
              <div>
                <p className="text-sm"><span className="font-medium text-foreground">Technical AI Checks: </span><span className="text-muted-foreground">Every scan now includes 4 technical checks automatically, no extra clicks. Check whether AI crawlers can find and read your site, with fix guides for what needs fixing.</span></p>
              </div>
              <div>
                <p className="text-sm"><span className="font-medium text-foreground">AI Discovery Levels: </span><span className="text-muted-foreground">Your AI score now clearly distinguishes between being Recommended vs. just Recognized. This helps you spot the critical gap: when AI knows your brand exists but doesn't actively suggest it to users.</span></p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">January 2026</p>
              <div>
                <p className="text-sm"><span className="font-medium text-foreground">Launch: </span><span className="text-muted-foreground">AI visibility scans backed by academic research from KDD, NeurIPS, and supporting preprints from leading institutions. Transparent methodology, no signup required.</span></p>
              </div>
            </div>
          </div>
        </section>

      </main>

      <footer className="max-w-4xl mx-auto px-4 pb-8">
        <ProjectDisclaimer />
      </footer>

    </div>
    </>
  );
}
